<script setup lang="ts">
import DisqualifyAfterSubmit from "@/components/common/OptionsSettings/DisqualifySettings/DisqualifyAfterSubmit.vue";
import DisqualifyImmediately from "@/components/common/OptionsSettings/DisqualifySettings/DisqualifyImmediately.vue";
import FacebookPixelEvent from "@/components/common/OptionsSettings/Facebook/FacebookPixelEvent.vue";
import FacebookPixelId from "@/components/common/OptionsSettings/Facebook/FacebookPixelId.vue";
import MessageStyling from "@/components/common/OptionsSettings/MessageStyling.vue";
import RichTextEditor from "@/components/text-editor/index.vue";
import { useAppStore } from "@/store/app";
import { deviceOptions, PRODUCT_TYPE, tooltipMessage } from "@/util/constants";
import { isTrueCustomObjectField } from "@/util/customObjectUtils";
import {
  UIAlert,
  UICard,
  UICheckbox,
  UIDivider,
  UIDropdown,
  UIInput,
  UISelect,
  UISwitch,
} from "@gohighlevel/ghl-ui";
import MessagePreview from "./MessagePreview.vue";
import OrderPreview from "./OrderPreview.vue";
import QuizResultPreview from "./QuizResultPreview.vue";
import UrlPreview from "./UrlPreview.vue";
// @ts-ignore
import { ChevronDownIcon, Monitor04Icon, Phone01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { computed, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const appStore = useAppStore();

// Utility to inject Google Font weights once per family
const loadFont = (family: string): void => {
  if (!family) {
    return;
  }
  const id = `dynamic-font-${family}`;
  if (document.getElementById(id)) {
    return;
  }
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  const sanitized = family.replace(/ /g, "+");
  // Load common weights so weight changes reflect without new requests
  link.href = `https://fonts.googleapis.com/css2?family=${sanitized}:wght@100;300;400;500;600;700;800;900&display=swap`;
  document.head.appendChild(link);
};

// Initialize the actionType to "2" (message) if it's not already set
onMounted(() => {
  // For quiz products, always default to quizResult if actionType is not already set to quizResult
  if (appStore.product === PRODUCT_TYPE.QUIZ) {
    if (!appStore.formAction.actionType) {
      appStore.formAction.actionType = "4"; // Default to quizResult for quiz
    }
  } else if (!appStore.formAction.actionType) {
    // For other products, default to message only if actionType is not set
    appStore.formAction.actionType = "2"; // Default to message for other products
  }

  // Initialize isEnabled if needed
  if (appStore.submitMessageStyle.isEnabled === undefined) {
    appStore.submitMessageStyle.isEnabled = false;
  }

  // Ensure submit message padding & margin defaults exist for all forms
  if (!(appStore.submitMessageStyle as any).padding) {
    (appStore.submitMessageStyle as any).padding = {
      top: 48,
      right: 48,
      bottom: 48,
      left: 48,
    };
  }
  if (!(appStore.submitMessageStyle as any).margin) {
    (appStore.submitMessageStyle as any).margin = {
      top: "auto",
      right: "auto",
      bottom: "auto",
      left: "auto",
    };
  }

  // Initial font load
  loadFont(appStore.submitMessageStyle.fontFamily);
});

// Reload font file if family changes
watch(
  () => appStore.submitMessageStyle.fontFamily,
  (newFont) => {
    loadFont(newFont);
  }
);

// Message content and type
const messageType = computed({
  get: () => {
    const actionType = String(appStore.formAction?.actionType);
    if (actionType === "1") {
      return "url";
    }
    if (actionType === "2") {
      return "message";
    }
    if (actionType === "3") {
      return "order";
    }
    if (actionType === "4") {
      return "quizResult";
    }
    return "message"; // Default to message
  },
  set: (value) => {
    if (value === "url") {
      appStore.formAction.actionType = "1";
    } else if (value === "message") {
      appStore.formAction.actionType = "2";
    } else if (value === "order") {
      appStore.formAction.actionType = "3";
    } else if (value === "quizResult") {
      appStore.formAction.actionType = "4";
    }
    appStore.anyUnsavedChanges = true;
    appStore.addToHistory();
  },
});

const isMobile = computed(() => appStore.deviceMode === "mobile");
const messageContent = computed({
  get: () =>
    isMobile.value
      ? (appStore.formAction as any).mobileThankyouText ?? appStore.formAction.thankyouText
      : appStore.formAction.thankyouText,
  set: (value: string) => {
    if (isMobile.value) {
      (appStore.formAction as any).mobileThankyouText = value;
    } else {
      appStore.formAction.thankyouText = value;
    }
    appStore.anyUnsavedChanges = true;
  },
});

// applyOpacity moved to util/color

const redirectUrl = computed({
  get: () => appStore.formAction.redirectUrl,
  set: (value) => {
    appStore.formAction.redirectUrl = value;
    appStore.anyUnsavedChanges = true;
  },
});

// Disable partial contact creation when custom object or business fields exist
const hasCOOrBusinessInSurvey = computed<boolean>(() => {
  if (appStore.product !== PRODUCT_TYPE.SURVEY) {
    return false;
  }
  const slides = appStore.slides || [];
  return slides.some((slide: any) =>
    (slide.slideData || []).some(
      (el: any) => isTrueCustomObjectField(el?.model) || el?.model === "business"
    )
  );
});

// Ensure it's turned off if currently on
watch(
  hasCOOrBusinessInSurvey,
  (shouldDisable) => {
    if (shouldDisable && appStore.builderConfig.enablePartialContactCreation) {
      appStore.builderConfig.enablePartialContactCreation = false;
      appStore.anyUnsavedChanges = true;
      appStore.addToHistory();
    }
  },
  { immediate: true }
);

// Form Settings
const enableStickyContact = computed(() => appStore.builderConfig.stickyContact || false);
const enableTimezone = computed(() => appStore.builderConfig.enableTimezone || false);
const enableGDPRFont = computed(() => appStore.builderConfig.isGDPRCompliant || false);

// Dynamic message type options based on product
const messageTypeOptions = computed(() => {
  const options = [
    { label: t("common.openUrl"), value: "url" },
    { label: t("common.message"), value: "message" },
    {
      label: t("common.payment.product.orderConfirmation"),
      value: "order",
      disabled: appStore.product === PRODUCT_TYPE.QUIZ,
    },
  ];

  // Add quiz result option for quiz products
  if (appStore.product === PRODUCT_TYPE.QUIZ) {
    options.push({ label: t("quiz.quizResult.resultTemplate"), value: "quizResult" });
  }

  return options;
});

// Common handler function for all toggle settings
const updateSetting = (settingKey: string, value: boolean) => {
  appStore.builderConfig[settingKey] = value;
  appStore.anyUnsavedChanges = true;
  appStore.addToHistory();
};

const updateGenerateSubmissionDocument = (value: boolean) => {
  appStore.builderConfig.generateSubmissionDocument = value;
  appStore.anyUnsavedChanges = true;
  appStore.addToHistory();
};

const updateGenerateSubmissionDocumentQuiz = (value: boolean) => {
  appStore.builderConfig.generateSubmissionDocument = value;
  if (value) {
    appStore.builderConfig.generateQuizSubmissionDocument = true;
    appStore.builderConfig.generateQuizResultDocument = true;
  } else {
    appStore.builderConfig.generateQuizSubmissionDocument = false;
    appStore.builderConfig.generateQuizResultDocument = false;
  }
  appStore.anyUnsavedChanges = true;
  appStore.addToHistory();
};

const syncGenerateSubmissionDocumentFromQuizCheckboxes = () => {
  if (
    !appStore.builderConfig.generateQuizSubmissionDocument &&
    !appStore.builderConfig.generateQuizResultDocument
  ) {
    appStore.builderConfig.generateSubmissionDocument = false;
  }
};

const updateQuizDocumentCheckbox = (
  key: "generateQuizSubmissionDocument" | "generateQuizResultDocument",
  value: boolean
) => {
  appStore.builderConfig[key] = value;
  syncGenerateSubmissionDocumentFromQuizCheckboxes();
  appStore.anyUnsavedChanges = true;
  appStore.addToHistory();
};

// Guarded updater for partial contact creation
const updatePartialContactSetting = (value: boolean) => {
  if (hasCOOrBusinessInSurvey.value) {
    if (appStore.builderConfig.enablePartialContactCreation) {
      appStore.builderConfig.enablePartialContactCreation = false;
      appStore.anyUnsavedChanges = true;
      appStore.addToHistory();
    }
    return;
  }
  updateSetting("enablePartialContactCreation", value);
};

// Handler for auto navigation (inverted value)
const updateAutoNavigation = (value: boolean) => {
  // The value is inverted - disableAutoNavigation = !value
  appStore.builderConfig.disableAutoNavigation = !value;
  appStore.anyUnsavedChanges = true;
  appStore.addToHistory();
};
</script>

<template>
  <div class="settings-container">
    <div class="settings-wrapper">
      <!-- On Submit Section -->
      <UICard class="mb-6">
        <template #header>
          <div class="border-b border-gray-200">
            <div class="flex w-full items-center justify-between py-1">
              <div class="text-lg font-semibold">{{ t("common.onSubmit") }}</div>
              <div v-if="messageType === 'message'" @click.stop>
                <UIDropdown
                  id="onsubmit-device-toggle"
                  :options="deviceOptions"
                  @select="(opt) => (appStore.deviceMode = opt as any)"
                >
                  <template #default>
                    <div class="flex items-center gap-0.5">
                      <Phone01Icon
                        v-if="appStore.deviceMode === 'mobile'"
                        class="h-4 w-4 cursor-pointer"
                      />
                      <Monitor04Icon v-else class="h-4 w-4 cursor-pointer" />
                      <ChevronDownIcon class="h-3 w-3" />
                    </div>
                  </template>
                </UIDropdown>
              </div>
            </div>
          </div>
        </template>

        <div class="flex flex-col gap-4">
          <!-- Message type selection and alerts -->
          <div>
            <p v-if="appStore.builderConfig?.conditionalLogic?.length" class="mb-3">
              <UIAlert id="warning-message" type="warning">
                <template #content>{{ t("common.conditionalLogicAlertOnSubmit") }}</template>
              </UIAlert>
            </p>

            <UISelect
              id="submit"
              :value="messageType"
              :options="messageTypeOptions"
              @update:value="messageType = $event"
            />

            <UIInput
              v-if="messageType === 'url'"
              id="submitMessage"
              :model-value="redirectUrl"
              :placeholder="redirectUrl || t('common.urlExamples.https')"
              class="my-2"
              @update:model-value="redirectUrl = $event"
              @blur="appStore.addToHistory()"
            >
            </UIInput>

            <p v-if="messageType === 'url'" class="mt-2 text-sm font-normal text-gray-600">
              {{ t("common.addUrlLike") }} {{ t("common.urlExamples.simple") }},
              {{ t("common.urlExamples.https") }}, {{ t("common.urlExamples.withPath") }}
              {{ t("common.or") }} {{ t("common.urlExamples.fullPath") }}.
            </p>

            <p v-if="messageType === 'order'" class="mt-2 text-sm font-normal text-gray-600">
              {{ t("common.payment.product.orderConfirmationWarning") }}
            </p>

            <p v-if="messageType === 'quizResult'" class="mt-2 text-sm font-normal text-gray-600">
              {{ t("quiz.quizResult.quizResultAlertMessage") }}
            </p>
          </div>

          <!-- Message editor + preview + styling (stacked vertically) -->
          <template v-if="messageType === 'message'">
            <section class="w-100 flex flex-col gap-3">
              <RichTextEditor
                v-model:modelValue="messageContent"
                :is-submit-message="true"
                custom-width="100%"
              >
              </RichTextEditor>
            </section>

            <MessagePreview :message-content="messageContent" />

            <section class="w-100 flex flex-col gap-3">
              <MessageStyling></MessageStyling>
            </section>
          </template>

          <!-- URL layout: input + preview stacked -->
          <template v-else-if="messageType === 'url'">
            <UrlPreview :redirect-url="redirectUrl" />
          </template>

          <!-- Order confirmation layout -->
          <template v-else-if="messageType === 'order'">
            <OrderPreview />
          </template>

          <!-- Quiz result layout -->
          <template v-else-if="messageType === 'quizResult'">
            <QuizResultPreview />
          </template>
        </div>
      </UICard>

      <!-- Disqualify Settings Section (Survey Only) -->
      <UICard
        v-if="appStore.product === PRODUCT_TYPE.SURVEY && (appStore.builder?.version ?? 0) < 4"
        class="mb-6"
      >
        <template #header>
          <div class="border-b border-gray-200">
            <div class="text-lg font-semibold">{{ t("survey.disqualifySettings") }}</div>
          </div>
        </template>

        <div class="grid grid-cols-1 gap-6 md:grid-cols-2" style="align-items: start">
          <!-- Left Column: Disqualify Immediately -->
          <div>
            <p class="mb-3 text-sm font-medium text-gray-700">
              {{ t("survey.disqualifyImmediatelyLabel") }}
            </p>
            <DisqualifyImmediately />
          </div>

          <!-- Right Column: Disqualify After Submit -->
          <div>
            <p class="mb-3 text-sm font-medium text-gray-700">
              {{ t("survey.disqualifyAfterSubmitLabel") }}
            </p>
            <DisqualifyAfterSubmit />
          </div>
        </div>
      </UICard>

      <!-- Facebook Pixel ID Section -->
      <UICard class="mb-6">
        <template #header>
          <div class="border-b border-gray-200">
            <div class="text-lg font-semibold">{{ t("common.facebookPixelID") }}</div>
          </div>
        </template>

        <div class="space-y-4">
          <FacebookPixelId />
        </div>
      </UICard>

      <!-- Facebook Pixel Events Section -->
      <UICard class="mb-6">
        <template #header>
          <div class="border-b border-gray-200">
            <div class="text-lg font-semibold">{{ t("common.facebookPixelEvents") }}</div>
          </div>
        </template>

        <div>
          <FacebookPixelEvent :product="PRODUCT_TYPE.FORM" />
        </div>
      </UICard>

      <!-- Form Settings Section -->
      <UICard class="mb-6">
        <template #header>
          <div class="border-b border-gray-200">
            <div class="text-lg font-semibold">
              {{
                appStore.product === PRODUCT_TYPE.FORM
                  ? t("form.formSettings")
                  : appStore.product === PRODUCT_TYPE.SURVEY
                  ? t("survey.surveySettings")
                  : t("quiz.quizSettings")
              }}
            </div>
          </div>
        </template>

        <div class="space-y-6">
          <!-- Survey-specific settings -->
          <template v-if="appStore.product === PRODUCT_TYPE.SURVEY">
            <!-- Create Contact on Partial Submission (Survey) -->
            <div class="flex items-start gap-4">
              <UISwitch
                id="settings-switch-partial-contact-survey"
                :value="appStore.builderConfig.enablePartialContactCreation"
                :disabled="hasCOOrBusinessInSurvey"
                class="mt-1 flex-shrink-0"
                @update:value="updatePartialContactSetting($event)"
              />
              <div>
                <p class="font-medium text-gray-700">
                  {{ t("common.createContactOnPartialSubmission") }}
                </p>
                <p class="mt-1 text-sm text-gray-500">
                  {{ t("survey.createContactOnPartialSubmissionSubText") }}
                </p>
              </div>
            </div>

            <UIDivider />
            <div v-if="!appStore?.builderConfig?.newFooter" class="flex items-start gap-4">
              <UISwitch
                id="settings-switch-back-button"
                :value="appStore.builderConfig.isBackButtonEnable"
                class="mt-1 flex-shrink-0"
                @update:value="updateSetting('isBackButtonEnable', $event)"
              />
              <div>
                <p class="font-medium text-gray-700">{{ t("survey.backButton") }}</p>
                <p class="mt-1 text-sm text-gray-500">{{ tooltipMessage.backButton }}</p>
              </div>
            </div>

            <UIDivider v-if="!appStore?.builderConfig?.newFooter" />

            <div class="flex items-start gap-4">
              <UISwitch
                id="settings-switch-auto-navigation"
                :value="!appStore.builderConfig.disableAutoNavigation"
                class="mt-1 flex-shrink-0"
                @update:value="updateAutoNavigation($event)"
              />
              <div>
                <p class="font-medium text-gray-700">{{ t("survey.autoNavigation") }}</p>
                <p class="mt-1 text-sm text-gray-500">{{ tooltipMessage.autoNavigation }}</p>
              </div>
            </div>

            <UIDivider />

            <div v-if="!appStore?.builderConfig?.newFooter" class="flex items-start gap-4">
              <UISwitch
                id="settings-switch-progress-bar"
                :value="appStore.builderConfig.isProgressBarEnabled"
                class="mt-1 flex-shrink-0"
                @update:value="updateSetting('isProgressBarEnabled', $event)"
              />
              <div>
                <p class="font-medium text-gray-700">{{ t("survey.progressBar") }}</p>
                <p class="mt-1 text-sm text-gray-500">{{ tooltipMessage.progressBar }}</p>
              </div>
            </div>

            <UIDivider v-if="!appStore?.builderConfig?.newFooter" />

            <div v-if="!appStore?.builderConfig?.newFooter" class="flex items-start gap-4">
              <UISwitch
                id="settings-switch-disable-animation"
                :value="appStore.builderConfig.isAnimationDisabled"
                class="mt-1 flex-shrink-0"
                @update:value="updateSetting('isAnimationDisabled', $event)"
              />
              <div>
                <p class="font-medium text-gray-700">{{ t("survey.disableAnimation") }}</p>
                <p class="mt-1 text-sm text-gray-500">{{ tooltipMessage.disableAnimation }}</p>
              </div>
            </div>

            <UIDivider v-if="!appStore?.builderConfig?.newFooter" />

            <div class="flex items-start gap-4">
              <UISwitch
                id="settings-switch-survey-scroll"
                :value="appStore.builderConfig.isSurveyScrollEnabled"
                class="mt-1 flex-shrink-0"
                @update:value="updateSetting('isSurveyScrollEnabled', $event)"
              />
              <div>
                <p class="font-medium text-gray-700">{{ t("survey.scrollToTop") }}</p>
                <p class="mt-1 text-sm text-gray-500">{{ tooltipMessage.scrollToTop }}</p>
              </div>
            </div>

            <UIDivider />
          </template>

          <!-- Quiz-specific settings -->
          <template v-else-if="appStore.product === PRODUCT_TYPE.QUIZ">
            <!-- Back Button (Quiz) -->
            <div class="flex items-start gap-4">
              <UISwitch
                id="settings-switch-back-button-quiz"
                :value="appStore.builderConfig.isBackButtonEnable"
                class="mt-1 flex-shrink-0"
                @update:value="updateSetting('isBackButtonEnable', $event)"
              />
              <div>
                <p class="font-medium text-gray-700">{{ t("survey.backButton") }}</p>
                <p class="mt-1 text-sm text-gray-500">{{ tooltipMessage.backButton }}</p>
              </div>
            </div>

            <UIDivider />

            <div class="flex items-start gap-4">
              <UISwitch
                id="settings-switch-disable-animation-quiz"
                :value="appStore.builderConfig.isAnimationDisabled"
                class="mt-1 flex-shrink-0"
                @update:value="updateSetting('isAnimationDisabled', $event)"
              />
              <div>
                <p class="font-medium text-gray-700">{{ t("survey.disableAnimation") }}</p>
                <p class="mt-1 text-sm text-gray-500">{{ tooltipMessage.disableAnimation }}</p>
              </div>
            </div>

            <UIDivider />

            <div class="flex items-start gap-4">
              <UISwitch
                id="settings-switch-survey-scroll-quiz"
                :value="appStore.builderConfig.isSurveyScrollEnabled"
                class="mt-1 flex-shrink-0"
                @update:value="updateSetting('isSurveyScrollEnabled', $event)"
              />
              <div>
                <p class="font-medium text-gray-700">{{ t("survey.scrollToTop") }}</p>
                <p class="mt-1 text-sm text-gray-500">{{ tooltipMessage.scrollToTop }}</p>
              </div>
            </div>

            <UIDivider />
          </template>

          <!-- Generate submission document (all product types, before Sticky Contact) -->
          <div class="flex flex-col gap-3">
            <div class="flex items-start gap-4">
              <UISwitch
                :id="
                  appStore.product === PRODUCT_TYPE.QUIZ
                    ? 'settings-switch-generate-submission-doc-quiz'
                    : 'settings-switch-generate-submission-doc'
                "
                :value="appStore.builderConfig.generateSubmissionDocument"
                class="mt-1 flex-shrink-0"
                @update:value="
                  appStore.product === PRODUCT_TYPE.QUIZ
                    ? updateGenerateSubmissionDocumentQuiz($event)
                    : updateGenerateSubmissionDocument($event)
                "
              />
              <div>
                <p class="font-medium text-gray-700">
                  {{ t("common.generateSubmissionDocument") }}
                </p>
                <p class="mt-1 text-sm text-gray-500">
                  {{ tooltipMessage.generateSubmissionDocument }}
                </p>
              </div>
            </div>
            <!-- Quiz-only: sub-checkboxes when toggle is on -->
            <template
              v-if="
                appStore.product === PRODUCT_TYPE.QUIZ &&
                appStore.builderConfig.generateSubmissionDocument
              "
            >
              <div class="ml-10 flex flex-col gap-2">
                <div class="flex items-start gap-4">
                  <UICheckbox
                    id="settings-checkbox-save-quiz-submission-pdf"
                    :checked="appStore.builderConfig.generateQuizSubmissionDocument"
                    class="mt-1 flex-shrink-0"
                    @update:checked="
                      updateQuizDocumentCheckbox('generateQuizSubmissionDocument', $event)
                    "
                  />
                  <p class="font-medium text-gray-700">{{ t("common.saveQuizSubmissionAsPdf") }}</p>
                </div>
                <div class="flex items-start gap-4">
                  <UICheckbox
                    id="settings-checkbox-save-quiz-result-pdf"
                    :checked="appStore.builderConfig.generateQuizResultDocument"
                    class="mt-1 flex-shrink-0"
                    @update:checked="
                      updateQuizDocumentCheckbox('generateQuizResultDocument', $event)
                    "
                  />
                  <p class="font-medium text-gray-700">{{ t("common.saveQuizResultAsPdf") }}</p>
                </div>
              </div>
            </template>
          </div>

          <UIDivider />

          <!-- Common settings for all product types -->
          <div class="flex items-start gap-4">
            <UISwitch
              id="settings-switch-sticky-contact"
              :value="enableStickyContact"
              class="mt-1 flex-shrink-0"
              @update:value="updateSetting('stickyContact', $event)"
            />
            <div>
              <p class="font-medium text-gray-700">{{ t("common.stickyContact") }}</p>
              <p class="mt-1 text-sm text-gray-500">{{ tooltipMessage.stickyContact }}</p>
            </div>
          </div>

          <UIDivider />

          <!-- Show submission in Conversations feed (Form & Survey only) -->
          <template v-if="appStore.product === PRODUCT_TYPE.FORM">
            <div class="flex items-start gap-4">
              <UISwitch
                id="settings-switch-show-submission-in-conversations"
                :value="appStore.builderConfig.showSubmissionInConversationsFeed || false"
                class="mt-1 flex-shrink-0"
                @update:value="updateSetting('showSubmissionInConversationsFeed', $event)"
              />
              <div>
                <p class="font-medium text-gray-700">
                  {{ t("common.showSubmissionInConversationsFeed") }}
                </p>
                <p class="mt-1 text-sm text-gray-500">
                  {{ tooltipMessage.showSubmissionInConversationsFeed }}
                </p>
              </div>
            </div>

            <UIDivider />
          </template>

          <div class="flex items-start gap-4">
            <UISwitch
              id="settings-switch-timezone"
              :value="enableTimezone"
              class="mt-1 flex-shrink-0"
              @update:value="updateSetting('enableTimezone', $event)"
            />
            <div>
              <p class="font-medium text-gray-700">{{ t("common.enableTimezone") }}</p>
              <p class="mt-1 text-sm text-gray-500">
                {{
                  appStore.product === PRODUCT_TYPE.FORM
                    ? tooltipMessage.enableTimezoneForm
                    : tooltipMessage.enableTimezoneSurvey
                }}
              </p>
            </div>
          </div>

          <UIDivider />

          <div class="flex items-start gap-4">
            <UISwitch
              id="settings-switch-gdpr-font"
              :value="enableGDPRFont"
              class="mt-1 flex-shrink-0"
              @update:value="updateSetting('isGDPRCompliant', $event)"
            />
            <div>
              <p class="font-medium text-gray-700">{{ t("common.useGDPRCompliantFont") }}</p>
            </div>
          </div>
        </div>
      </UICard>
    </div>
  </div>
</template>

<style scoped>
.settings-container {
  height: 100%;
  width: 100%;
  overflow-y: auto;
  padding: 1.5rem;
  background-color: #f2f4f7;
  display: flex;
  flex-direction: column;
}

.settings-wrapper {
  width: 850px;
  max-width: 850px;
  margin: 0 auto;
  flex: 1;
  padding-bottom: 100px;
}

.small-alert {
  font-size: 0.875rem;
}

.preview-container {
  height: auto;
  padding-bottom: 8px;
}

.preview-container--url,
.preview-container--quizResult {
  height: 240px;
  margin-bottom: 16px;
}

.preview-container--url,
.preview-container--quizResult {
  margin-bottom: 24px;
}

.preview-container--order {
  margin-bottom: 24px;
}

.message-content {
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bg-container {
  position: relative;
  height: 100%;
}

.settings-container p {
  margin-bottom: 0;
}
</style>

<style>
/* Styles to hide n-input__suffix spacing */
#redirect-url-preview > div.n-input-wrapper > div.n-input__suffix {
  display: none;
}

#submitMessage > div.n-input-wrapper > div.n-input__suffix {
  display: none;
}

/* Also adjust the input padding since suffix is hidden */
#redirect-url-preview > div.n-input-wrapper > div.n-input__input > input.n-input__input-el,
#submitMessage > div.n-input-wrapper > div.n-input__input > input.n-input__input-el {
  padding-right: 12px !important;
}
</style>
